import { memo, type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import { Platform, StyleSheet, View, type ViewStyle } from 'react-native'
import type RNMapView from 'react-native-maps'
import { getRotationStyle } from '@/lib/movement-utils'
import MapView, { Circle, Marker } from '@/ui/MapView'

const WEB_MAP_OPTIONS = {
    fullscreenControl: false,
    streetViewControl: false,
    mapTypeControl: false,
    zoomControl: false,
    disableDefaultUI: true,
    clickableIcons: false,
}

export interface MiniMapCoord {
    latitude: number
    longitude: number
}

export interface MiniMapCircle {
    latitude: number
    longitude: number
    radius: number
    strokeColor: string
    fillColor: string
}

export interface MiniMapMarker {
    coordinate: MiniMapCoord
    // When `content` is provided, it's rendered as the marker's custom view
    // (mobile: react-native-maps Marker children; web: OverlayView children).
    // Otherwise the platform's default pin is used, tinted by `pinColor`.
    pinColor?: string
    content?: ReactNode
    // Rotation in degrees applied to the custom content (for movement
    // heading). Ignored when `content` is absent.
    rotation?: number
    // If true, rotations in the ±90..±180 range are flipped so icons
    // don't render upside-down — matches movement-utils behavior.
    shouldFlip?: boolean
}

interface Props {
    // Coordinates to fit the map to. First element is used as the initialRegion focal point.
    // fitToCoordinates runs on onMapReady so zoom is applied correctly (initialRegion alone
    // is unreliable on Google Maps web + iOS).
    coordinates: MiniMapCoord[]
    markers?: MiniMapMarker[]
    circles?: MiniMapCircle[]
    minDelta?: number
    edgePadding?: number
    style?: ViewStyle
}

const DEFAULT_MIN_DELTA = 0.003

// Convert a latitudeDelta (what react-native-maps wants) into a Google Maps zoom
// level (what the web maps package wants). Google Maps zoom 0 shows the whole
// world (~360° lat); each +1 halves the range.
function deltaToZoom(delta: number): number {
    const z = Math.round(Math.log2(360 / delta))
    return Math.max(0, Math.min(21, z))
}

export const MiniMap = memo(function MiniMap({
    coordinates,
    markers = [],
    circles = [],
    minDelta = DEFAULT_MIN_DELTA,
    edgePadding = 20,
    style,
}: Props) {
    const mapRef = useRef<RNMapView>(null)

    const initialRegion = useMemo(() => {
        const first = coordinates[0]
        if (!first) return undefined
        return {
            latitude: first.latitude,
            longitude: first.longitude,
            latitudeDelta: minDelta,
            longitudeDelta: minDelta,
        }
    }, [coordinates, minDelta])

    // On web the underlying @teovilla/react-native-web-maps library hardcodes
    // `zoom: props.initialCamera?.zoom || 3` and ignores initialRegion deltas.
    // Supplying initialCamera with the right zoom makes the map load at the
    // correct level on first render — no post-mount animation needed.
    const initialCamera = useMemo(() => {
        const first = coordinates[0]
        if (!first) return undefined
        return {
            center: { latitude: first.latitude, longitude: first.longitude },
            zoom: deltaToZoom(minDelta),
            heading: 0,
            pitch: 0,
            altitude: 0,
        }
    }, [coordinates, minDelta])

    const isReadyRef = useRef(false)

    const applyCamera = useCallback(() => {
        if (coordinates.length === 0 || !mapRef.current) return
        if (coordinates.length === 1) {
            mapRef.current.animateToRegion(
                {
                    latitude: coordinates[0].latitude,
                    longitude: coordinates[0].longitude,
                    latitudeDelta: minDelta,
                    longitudeDelta: minDelta,
                },
                300
            )
            return
        }
        mapRef.current.fitToCoordinates(coordinates, {
            edgePadding: {
                top: edgePadding,
                right: edgePadding,
                bottom: edgePadding,
                left: edgePadding,
            },
            animated: true,
        })
    }, [coordinates, minDelta, edgePadding])

    const onMapReady = useCallback(() => {
        isReadyRef.current = true
        applyCamera()
    }, [applyCamera])

    // Re-pan when coordinates change after the initial render so mini-maps
    // follow the user as new movement events arrive.
    useEffect(() => {
        if (!isReadyRef.current) return
        applyCamera()
    }, [applyCamera])

    if (coordinates.length === 0) {
        return <View style={[styles.container, style]} />
    }

    return (
        <View style={[styles.container, style]} pointerEvents="none">
            <MapView
                ref={mapRef}
                style={styles.map}
                initialRegion={initialRegion}
                {...(Platform.OS === 'web' && initialCamera
                    ? ({ initialCamera } as Record<string, unknown>)
                    : {})}
                onMapReady={onMapReady}
                showsUserLocation={false}
                showsMyLocationButton={false}
                showsCompass={false}
                showsTraffic={false}
                showsBuildings={false}
                showsIndoors={false}
                toolbarEnabled={false}
                scrollEnabled={false}
                zoomEnabled={false}
                rotateEnabled={false}
                pitchEnabled={false}
                {...({ options: WEB_MAP_OPTIONS } as Record<string, unknown>)}
            >
                {circles.map(c => (
                    <Circle
                        key={`circle-${c.latitude}-${c.longitude}-${c.radius}`}
                        center={{ latitude: c.latitude, longitude: c.longitude }}
                        radius={c.radius}
                        strokeColor={c.strokeColor}
                        fillColor={c.fillColor}
                        strokeWidth={2}
                    />
                ))}
                {markers.map(m => (
                    <Marker
                        key={`marker-${m.coordinate.latitude}-${m.coordinate.longitude}`}
                        coordinate={m.coordinate}
                        anchor={m.content ? { x: 0.5, y: 0.5 } : undefined}
                        pinColor={m.content ? undefined : m.pinColor}
                        tracksViewChanges={!!m.content}
                        flat
                    >
                        {m.content ? (
                            <View
                                style={
                                    m.rotation
                                        ? getRotationStyle(m.rotation, m.shouldFlip)
                                        : undefined
                                }
                            >
                                {m.content}
                            </View>
                        ) : null}
                    </Marker>
                ))}
            </MapView>
        </View>
    )
})

const styles = StyleSheet.create({
    container: {
        borderRadius: 8,
        overflow: 'hidden',
    },
    map: { flex: 1 },
})
